import Spinner from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { usePayrollPermissions } from "@/modules/payrolls/hooks/usePayrollPermissions";
import { LucideIcon } from "lucide-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface PayrollConfirmButtonProps {
  translationKey: string;
  open: boolean;
  isPending: boolean;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  icon: LucideIcon;
  disabled?: boolean;
}

export const PayrollConfirmButton = memo(
  ({
    open,
    onOpenChange,
    translationKey,
    onConfirm,
    isPending,
    isLoading,
    icon: Icon,
    disabled: disabledFromProps = false,
  }: PayrollConfirmButtonProps) => {
    const { t } = useTranslation();
    const permissions = usePayrollPermissions();
    const disabled =
      disabledFromProps || !permissions.can_create || permissions.isLoading;

    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" disabled={disabled || isLoading}>
            {isLoading ? (
              <Spinner className="w-5 h-5 mr-1" />
            ) : (
              <Icon className="w-5 h-5 mr-1" />
            )}
            {t(`${translationKey}.trigger`)}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <Icon className="w-5 h-5 mr-3" />
              {t(`${translationKey}.title`)}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {t(`${translationKey}.content`)}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>
              {t(`${translationKey}.no`)}
            </AlertDialogCancel>
            <AlertDialogAction onClick={onConfirm} disabled={isPending}>
              {isPending && <Spinner className="w-5 h-5" />}
              {!isPending && <Icon className="w-5 h-5 mr-1" />}
              {t(`${translationKey}.yes`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);
